import { CheckboxProps, Indicator, Root } from '@radix-ui/react-checkbox'
import { IconCheck } from '@tabler/icons-react'
import { Ref } from 'react'

import { MinusOutlined } from '../../../icons'
import { SizeOptions } from '../../../types/types'
import { astraTwMerge } from '../../Utilities/astraTwMerge'

type Status = 'unchecked' | 'checked' | 'indeterminate'
type State = 'rest' | 'hover' | 'active' | 'disabled'

export type AstraCheckboxProps = {
    /**
     * Optional Data test id, used for testing
     */
    'data-test'?: string
    /**
     * Size of the checkbox
     */
    size?: SizeOptions
    /**
     * Ref to the button element
     */
    ref?: Ref<HTMLButtonElement>
} & CheckboxProps

const checkboxStyles: Record<State, Record<Status, string>> = {
    rest: {
        unchecked: 'bg-surface-base rounded border border-strong',
        checked: 'rounded data-[state=checked]:bg-brand-strong-selected',
        indeterminate: 'rounded data-[state=indeterminate]:bg-brand-strong-selected',
    },
    hover: {
        unchecked: 'hover:border-strong',
        checked: 'data-[state=checked]:hover:bg-brand-strong-hover',
        indeterminate: 'data-[state=indeterminate]:hover:bg-brand-strong-hover data-[state=indeterminate]:hover:border-hover',
    },
    active: {
        unchecked: 'active:bg-neutral-subtle-active active:border-hover',
        checked: 'data-[state=checked]:active:bg-brand-strong-hover data-[state=checked]:active:border-hover',
        indeterminate: 'data-[state=indeterminate]:active:bg-neutral-active data-[state=indeterminate]:active:border-hover',
    },
    disabled: {
        unchecked: 'disabled:bg-surface-disabled disabled:border-base',
        checked: 'border border-base data-[state=checked]:disabled:bg-surface-disabled',
        indeterminate: 'border border-base data-[state=indeterminate]:disabled:bg-surface-disabled',
    },
}

const iconStyles = 'stroke-2 stroke-current text-inverted group-disabled:stroke-neutral-300 group-data-[state=indeterminate]:stroke-2'

const stateStyles: Record<Status, string> = {
    unchecked: `${checkboxStyles.rest.unchecked} ${checkboxStyles.hover.unchecked} ${checkboxStyles.active.unchecked} ${checkboxStyles.disabled.unchecked}`,
    checked: `${checkboxStyles.rest.checked} ${checkboxStyles.hover.checked} ${checkboxStyles.active.checked} ${checkboxStyles.disabled.checked}`,
    indeterminate: `${checkboxStyles.rest.indeterminate} ${checkboxStyles.hover.indeterminate} ${checkboxStyles.active.indeterminate} ${checkboxStyles.disabled.indeterminate}`,
}

const sizeStyles: Record<SizeOptions, string> = {
    small: 'h-4 min-w-4',
    medium: 'h-5 min-w-5',
    large: 'h-6 min-w-6',
}

const iconSizeStyles: Record<SizeOptions, string> = {
    small: 'size-3',
    medium: 'size-4',
    large: 'size-5',
}

/**
 * A checkbox records a yes/no choice that takes effect when the surrounding form is submitted, or that selects an
 * item in a list. It also supports an `indeterminate` state for a parent whose children are partly selected.
 * For a setting that applies immediately when toggled, use AstraSwitch instead.
 *
 * @summary for a form choice or list selection, including partly-selected parents
 */
export const AstraCheckbox = ({ ref, checked, disabled = false, size = 'medium', ...props }: AstraCheckboxProps) => {
    const status: Status = checked === 'indeterminate' ? 'indeterminate' : checked ? 'checked' : 'unchecked'

    return (
        <Root
            className={astraTwMerge('group flex items-center justify-center', sizeStyles[size], stateStyles[status])}
            checked={checked}
            tabIndex={-1}
            disabled={disabled}
            ref={ref}
            {...props}
        >
            <Indicator>
                {checked === 'indeterminate' && <MinusOutlined className={astraTwMerge(iconSizeStyles[size], iconStyles)} />}
                {checked === true && <IconCheck className={astraTwMerge(iconSizeStyles[size], iconStyles)} />}
            </Indicator>
        </Root>
    )
}
